import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { SITE, SERVICES } from "@/lib/site";
import { api, formatApiError } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast.error("Ad ve mesaj alanları zorunludur");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Mesajınız iletildi. En kısa sürede dönüş yapacağız.");
      setForm({ name: "", phone: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="px-6 md:px-12 pt-28 pb-12">
        <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#d4af37]">— İletişim</p>
        <h1 className="mt-3 font-display font-extrabold tracking-tighter text-5xl md:text-7xl lg:text-[8rem] leading-[0.85]">
          Konuşalım. <br /><span className="text-[#d4af37]">Keşif ücretsiz.</span>
        </h1>
      </section>

      <section className="px-6 md:px-12 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-4 space-y-6">
          <div className="glass p-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#d4af37] mb-4">Telefon</p>
            <div className="space-y-3">
              {SITE.phones.map((p) => (
                <a key={p.value} href={`tel:${p.value}`} data-testid={`contact-phone-${p.value}`} className="flex items-center gap-3 text-white hover:text-[#d4af37] transition">
                  <Phone className="h-5 w-5 text-[#d4af37]" />
                  <span className="font-display text-2xl">{p.label}</span>
                </a>
              ))}
            </div>
          </div>
          <div className="glass p-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#d4af37] mb-4">E-Posta</p>
            <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 text-white hover:text-[#d4af37] transition">
              <Mail className="h-5 w-5 text-[#d4af37]" /> {SITE.email}
            </a>
          </div>
          <div className="glass p-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#d4af37] mb-4">Adres</p>
            <p className="flex items-start gap-3 text-white/85">
              <MapPin className="h-5 w-5 text-[#d4af37] shrink-0 mt-1" /> <span>{SITE.address}</span>
            </p>
            <p className="text-white/45 text-xs mt-2 font-mono">39.7767°N · 30.5206°E · TR-26</p>
          </div>
        </aside>
        <form onSubmit={submit} className="lg:col-span-8 border border-white/8 bg-[#0a0a0a] p-7 md:p-10" data-testid="contact-form">
          <h2 className="font-display text-3xl md:text-4xl tracking-tight">Ücretsiz Keşif Talebi</h2>
          <p className="text-white/55 mt-2 text-sm">Detaylı bilgileri paylaşın, ekibimiz 24 saat içinde dönüş yapsın.</p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">Ad Soyad *</label>
              <input data-testid="contact-input-name" value={form.name} onChange={set("name")} className="mt-2 w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] py-3 outline-none text-white" required />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">Telefon</label>
              <input data-testid="contact-input-phone" value={form.phone} onChange={set("phone")} className="mt-2 w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] py-3 outline-none text-white" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">E-Posta</label>
              <input type="email" data-testid="contact-input-email" value={form.email} onChange={set("email")} className="mt-2 w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] py-3 outline-none text-white" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">Hizmet</label>
              <select data-testid="contact-input-subject" value={form.subject} onChange={set("subject")} className="mt-2 w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] py-3 outline-none text-white">
                <option value="" className="bg-[#0a0a0a]">Seçiniz</option>
                {SERVICES.map((s) => (
                  <option key={s.slug} value={s.title} className="bg-[#0a0a0a]">{s.title}</option>
                ))}
                <option value="Diğer" className="bg-[#0a0a0a]">Diğer</option>
              </select>
            </div>
          </div>
          <div className="mt-6">
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">Mesaj *</label>
            <textarea data-testid="contact-input-message" value={form.message} onChange={set("message")} rows={5} className="mt-2 w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] py-3 outline-none text-white resize-none" required />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit"
            className="mt-8 inline-flex items-center gap-3 bg-[#d4af37] text-[#121212] hover:bg-white disabled:opacity-60 px-7 py-4 font-display uppercase tracking-[0.18em] text-sm transition"
          >
            {sending ? "Gönderiliyor..." : (<><Send className="h-4 w-4" /> Mesajı Gönder</>)}
          </button>
        </form>
      </section>
    </div>
  );
}
